import aiService from './aiService';
import supabase from './supabase';

class AIServiceRecommendations {
  // Generate services for a custom business type using AI
  async generateServicesForBusinessType(businessType) {
    try {
      // First check if we already have services for this business type
      const existingServices = await this.getExistingServices(businessType);
      if (existingServices.length > 0) {
        return { success: true, data: existingServices, fromCache: true };
      }

      // Generate new services using AI
      const prompt = `Generate 6-8 specific service types for a "${businessType}" business. 
      
      Return ONLY a JSON array of objects with this exact format:
      [
        {
          "name": "Service Name",
          "description": "Brief description of what this service includes"
        }
      ]
      
      Make the services specific and practical for this business type. Focus on common services this type of business would actually offer to customers.`;

      const aiResponse = await aiService.generateText(prompt);
      
      if (!aiResponse.success) {
        throw new Error('AI service failed');
      }

      // Parse AI response
      let services;
      try {
        let cleanedResponse = aiResponse.data.trim();
        
        // Try to extract JSON from the response if it's wrapped in other text
        const jsonMatch = cleanedResponse.match(/\[\s*\{[\s\S]*\}\s*\]/);
        if (jsonMatch) {
          cleanedResponse = jsonMatch[0];
        }
        
        // Remove any markdown code blocks
        cleanedResponse = cleanedResponse.replace(/```json\n?/g, '').replace(/```\n?/g, '');
        
        services = JSON.parse(cleanedResponse);
      } catch (parseError) {
        console.error('JSON parsing failed:', parseError);
        console.log('Raw AI response:', aiResponse.data);
        
        // Fallback to manual parsing if JSON is malformed
        services = this.parseServicesFromText(aiResponse.data);
      }

      // Validate and store services
      const validServices = services.filter(service => 
        service.name && service.name.trim().length > 0
      );

      if (validServices.length === 0) {
        throw new Error('No valid services generated');
      }

      // Store services in database for future use
      await this.storeGeneratedServices(businessType, validServices);

      return { success: true, data: validServices, fromCache: false };

    } catch (error) {
      console.error('Error generating services for business type:', error);
      
      // Return fallback generic services
      const fallbackServices = [
        { name: 'Consultation', description: 'Initial consultation and assessment' },
        { name: 'Installation', description: 'Professional installation services' },
        { name: 'Repair', description: 'Repair and troubleshooting services' },
        { name: 'Maintenance', description: 'Regular maintenance and upkeep' },
        { name: 'Emergency Service', description: '24/7 emergency response' }
      ];

      return { success: true, data: fallbackServices, fromCache: false, fallback: true };
    }
  }

  // Get existing services for a business type
  async getExistingServices(businessType) {
    try {
      const { data, error } = await supabase
        .from('custom_business_services')
        .select('id, service_name, service_description, usage_count')
        .eq('business_type', businessType.toLowerCase())
        .order('usage_count', { ascending: false });

      if (error) throw error;

      return data.map(service => ({
        id: service.id,
        name: service.service_name,
        description: service.service_description,
        usageCount: service.usage_count
      }));

    } catch (error) {
      console.error('Error fetching existing services:', error);
      return [];
    }
  }

  // Store AI-generated services in database
  async storeGeneratedServices(businessType, services) {
    try {
      const servicesToInsert = services.map(service => ({
        business_type: businessType.toLowerCase(),
        service_name: service.name,
        service_description: service.description,
        ai_generated: true,
        usage_count: 1
      }));

      const { error } = await supabase
        .from('custom_business_services')
        .insert(servicesToInsert);

      if (error) throw error;

      console.log(`Stored ${services.length} services for business type: ${businessType}`);

    } catch (error) {
      console.error('Error storing generated services:', error);
    }
  }

  // Increment usage count when a service is selected
  async incrementServiceUsage(serviceId) {
    try {
      const { error } = await supabase
        .rpc('increment_service_usage', { service_id: serviceId });

      if (error) throw error;

    } catch (error) {
      console.error('Error incrementing service usage:', error);
    }
  }

  // Parse services from text if JSON parsing fails
  parseServicesFromText(text) {
    const services = [];
    
    try {
      // First try to manually extract JSON objects from the text
      const jsonObjects = [];
      const lines = text.split('\n');
      let currentObject = '';
      let inObject = false;
      
      for (const line of lines) {
        const trimmed = line.trim();
        
        // Skip JSON syntax lines that aren't part of object content
        if (trimmed === '[' || trimmed === ']' || trimmed === '{' || trimmed === '},' || trimmed === '}') {
          if (trimmed === '{') {
            inObject = true;
            currentObject = '';
          } else if (trimmed === '}' || trimmed === '},') {
            inObject = false;
            if (currentObject.trim()) {
              jsonObjects.push(currentObject.trim());
            }
            currentObject = '';
          }
          continue;
        }
        
        // Extract name and description from lines like: "name": "Service Name"
        if (inObject && trimmed.includes(':')) {
          const nameMatch = trimmed.match(/["']?name["']?\s*:\s*["']([^"']+)["']?/);
          const descMatch = trimmed.match(/["']?description["']?\s*:\s*["']([^"']+)["']?/);
          
          if (nameMatch) {
            currentObject += `name: ${nameMatch[1]}; `;
          }
          if (descMatch) {
            currentObject += `description: ${descMatch[1]}; `;
          }
        }
      }
      
      // Convert extracted objects to services
      for (const obj of jsonObjects) {
        const nameMatch = obj.match(/name:\s*([^;]+)/);
        const descMatch = obj.match(/description:\s*([^;]+)/);
        
        if (nameMatch) {
          services.push({
            name: nameMatch[1].trim(),
            description: descMatch ? descMatch[1].trim() : 'Professional service'
          });
        }
      }
      
      // If we didn't extract any services, try the old method
      if (services.length === 0) {
        for (const line of lines) {
          const trimmed = line.trim();
          if (trimmed && !trimmed.startsWith('[') && !trimmed.startsWith(']') && 
              !trimmed.startsWith('{') && !trimmed.startsWith('}') && 
              !trimmed.includes('```') && !trimmed.includes('json')) {
            
            // Try to extract service name and description from various formats
            const match = trimmed.match(/["']?([^"':,]+)["']?\s*[:,-]?\s*["']?([^"']+)?["']?/);
            if (match && match[1] && match[1].length > 2) {
              services.push({
                name: match[1].trim(),
                description: match[2] ? match[2].trim() : 'Professional service'
              });
            }
          }
        }
      }
      
    } catch (error) {
      console.error('Error parsing services from text:', error);
      
      // Final fallback - return generic services
      return [
        { name: 'Consultation', description: 'Initial consultation and assessment' },
        { name: 'Installation', description: 'Professional installation services' },
        { name: 'Repair', description: 'Repair and troubleshooting services' },
        { name: 'Maintenance', description: 'Regular maintenance and upkeep' }
      ];
    }
    
    return services.slice(0, 8); // Limit to 8 services
  }

  // Get recommended services based on standard business type
  getRecommendedServices(businessType) {
    const serviceMap = {
      'hvac': [
        { name: 'HVAC Installation', description: 'Complete heating and cooling system installation' },
        { name: 'HVAC Repair', description: 'Emergency and scheduled HVAC repairs' },
        { name: 'HVAC Maintenance', description: 'Regular system maintenance and tune-ups' },
        { name: 'Duct Cleaning', description: 'Air duct cleaning and sanitization' },
        { name: 'Indoor Air Quality', description: 'Air purification and filtration services' }
      ],
      'plumbing': [
        { name: 'Emergency Plumbing', description: '24/7 emergency plumbing repairs' },
        { name: 'Drain Cleaning', description: 'Clogged drain and sewer line cleaning' },
        { name: 'Pipe Installation', description: 'New pipe installation and replacement' },
        { name: 'Water Heater Service', description: 'Water heater repair and replacement' },
        { name: 'Leak Detection', description: 'Professional leak detection and repair' }
      ],
      'electrical': [
        { name: 'Electrical Repair', description: 'Residential and commercial electrical repairs' },
        { name: 'Panel Upgrades', description: 'Electrical panel installation and upgrades' },
        { name: 'Outlet Installation', description: 'New outlet and switch installation' },
        { name: 'Lighting Installation', description: 'Indoor and outdoor lighting installation' },
        { name: 'Electrical Inspection', description: 'Safety inspections and code compliance' }
      ],
      'cleaning': [
        { name: 'Regular House Cleaning', description: 'Weekly, bi-weekly, or monthly house cleaning' },
        { name: 'Deep Cleaning', description: 'Comprehensive deep cleaning service' },
        { name: 'Move-in/Move-out Cleaning', description: 'Specialized cleaning for relocations' },
        { name: 'Commercial Cleaning', description: 'Office and commercial space cleaning' },
        { name: 'Post-Construction Cleanup', description: 'Construction site cleanup services' }
      ],
      'landscaping': [
        { name: 'Lawn Maintenance', description: 'Regular lawn mowing and care' },
        { name: 'Landscape Design', description: 'Custom landscape design and installation' },
        { name: 'Tree Services', description: 'Tree trimming, removal, and care' },
        { name: 'Irrigation Installation', description: 'Sprinkler system installation and repair' },
        { name: 'Seasonal Cleanup', description: 'Spring and fall yard cleanup' }
      ],
      'roofing': [
        { name: 'Roof Repair', description: 'Emergency and scheduled roof repairs' },
        { name: 'Roof Replacement', description: 'Complete roof replacement services' },
        { name: 'Roof Inspection', description: 'Professional roof inspection and assessment' },
        { name: 'Gutter Services', description: 'Gutter installation, repair, and cleaning' },
        { name: 'Emergency Tarping', description: 'Emergency roof protection services' }
      ],
      'painting': [
        { name: 'Interior Painting', description: 'Professional interior painting services' },
        { name: 'Exterior Painting', description: 'Exterior house and building painting' },
        { name: 'Cabinet Refinishing', description: 'Kitchen and bathroom cabinet refinishing' },
        { name: 'Deck Staining', description: 'Deck and fence staining services' },
        { name: 'Drywall Repair', description: 'Drywall repair and texture matching' }
      ],
      'handyman': [
        { name: 'General Repairs', description: 'Various home repair and maintenance tasks' },
        { name: 'Furniture Assembly', description: 'Professional furniture assembly service' },
        { name: 'Home Improvements', description: 'Small home improvement projects' },
        { name: 'Fixture Installation', description: 'Light fixtures, fans, and hardware installation' },
        { name: 'Pressure Washing', description: 'Exterior cleaning and pressure washing' }
      ],
      'pest-control': [
        { name: 'General Pest Control', description: 'Comprehensive pest elimination and prevention' },
        { name: 'Termite Treatment', description: 'Termite inspection and treatment services' },
        { name: 'Rodent Control', description: 'Mouse and rat elimination services' },
        { name: 'Bed Bug Treatment', description: 'Professional bed bug elimination' },
        { name: 'Preventive Treatment', description: 'Regular pest prevention services' }
      ],
      'appliance-repair': [
        { name: 'Refrigerator Repair', description: 'Refrigerator and freezer repair services' },
        { name: 'Washer/Dryer Repair', description: 'Washing machine and dryer repair' },
        { name: 'Dishwasher Repair', description: 'Dishwasher troubleshooting and repair' },
        { name: 'Oven/Range Repair', description: 'Oven, stove, and range repair services' },
        { name: 'Appliance Installation', description: 'New appliance installation and setup' }
      ]
    };

    return serviceMap[businessType] || [];
  }
}

export default new AIServiceRecommendations(); 